import { useMemo, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { MarkdownBlocks } from "@/lib/miniMarkdown";
import faceDataMd from "@/content/personalLegal/face-data-policy.md?raw";
import { publishedBlocks } from "@/pages/personal/PersonalLegalPage";
import { faceDataPolicyHref, getFaceDataPolicyCopy } from "@/content/faceDataPolicy";

/**
 * The full Photohawk Family face data policy at /face-data-policy/family: official (v1.1, 28 October 2026; v1.0 from 20 October 2026) and public
 * in production, unlike the app's other legal drafts under /personal/*. The text is photohawk-personal
 * legal/face-data-policy.md (scripts/sync-personal-legal.sh), English only, without its notes for counsel. The app
 * links photohawk.com/personal/face-data-policy, a 301 to here.
 */
const PROSE =
  "text-muted-foreground leading-relaxed [&_a]:text-hawk-orange [&_a]:underline [&_h1]:mb-4 [&_h1]:font-display [&_h1]:text-3xl md:[&_h1]:text-4xl [&_h1]:font-bold [&_h1]:text-foreground [&_h2]:mb-3 [&_h2]:mt-10 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-foreground [&_h3]:mt-6 [&_h3]:font-semibold [&_h3]:text-foreground [&_strong]:text-foreground [&_li]:my-1.5 [&_ol]:my-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:my-4 [&_ul]:my-4 [&_ul]:list-disc [&_ul]:pl-6 [&_hr]:my-8 [&_hr]:border-border [&_.md-table]:my-5 [&_.md-table]:overflow-x-auto [&_table]:w-full [&_table]:border-collapse [&_table]:text-sm [&_td]:border [&_td]:border-border [&_td]:p-2.5 [&_td]:align-top [&_th]:border [&_th]:border-border [&_th]:bg-muted [&_th]:p-2.5 [&_th]:text-left [&_th]:text-foreground";

/** photohawk.com links in the text → this site's routes. */
const link = (href: string, children: ReactNode, key: string) => {
  const m = /^https:\/\/photohawk\.com(\/[\w/-]*)$/.exec(href);
  return m ? (
    <Link key={key} to={m[1]}>
      {children}
    </Link>
  ) : (
    <a key={key} href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
};

const FamilyFaceDataPolicy = () => {
  const { i18n } = useTranslation();
  const copy = getFaceDataPolicyCopy(i18n.language).familyPage;
  const blocks = useMemo(() => publishedBlocks(faceDataMd), []);
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{copy.seoTitle}</title>
        <meta name="description" content={copy.description} />
      </Helmet>
      <AppHeader variant="marketing" />
      <main className="container mx-auto px-4 pt-28 pb-20 max-w-3xl">
        <p className="mb-6 text-sm">
          <Link to={faceDataPolicyHref("family")} className="text-hawk-orange underline hover:no-underline">
            ← {copy.back}
          </Link>
        </p>
        {copy.englishOnly && (
          <p lang={i18n.language} className="mb-6 text-sm text-muted-foreground">
            {copy.englishOnly}
          </p>
        )}
        <article className={PROSE} lang="en" data-testid="family-face-data-policy">
          <MarkdownBlocks blocks={blocks} link={link} />
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default FamilyFaceDataPolicy;
